// Yes / No / N/A (or Above Target / Below Target / N/A) question checklist, grouped by section.
// Mirrors the mockup's `qa` component: .people-document-checklist, -question-group(-title), -answer-row.
import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import FontFamily from '../../../../../constants/fonts';
import { DocumentSection } from './DocumentBlocks';
import { DocColors } from './tokens';

export interface ChecklistRow {
  id: string;
  label: string;
  options: string[];
}

export interface ChecklistGroup {
  key: string;
  title?: string;
  rows: ChecklistRow[];
}

// Header cells and answer pills share one width per option so the columns stay aligned,
// including the long "Above Target/On Target" label.
const optionWidth = (opt: string) => Math.max(58, opt.length * 7 + 24);

interface QuestionChecklistProps {
  title: string;
  helper?: string;
  groups: ChecklistGroup[];
  answers: Record<string, string>;
  onAnswer?: (rowId: string, answer: string) => void;
  readOnly?: boolean;
}

const QuestionChecklist: React.FC<QuestionChecklistProps> = ({ title, helper, groups, answers, onAnswer, readOnly }) => (
  <DocumentSection title={title} hint={helper}>
    <View style={styles.checklist}>
      {groups.map((group, groupIdx) => (
        <View key={group.key} style={groupIdx > 0 && styles.groupDivider}>
          {!!group.title && (
            <View style={styles.groupTitleRow}>
              <Text style={styles.groupTitle}>{group.title}</Text>
              <View style={styles.optionCells}>
                {(group.rows[0]?.options ?? []).map((opt) => (
                  <Text key={opt} style={[styles.optionHeader, { width: optionWidth(opt) }]} numberOfLines={1}>{opt}</Text>
                ))}
              </View>
            </View>
          )}
          {group.rows.map((row, rowIdx) => (
            <View key={row.id} style={[styles.answerRow, rowIdx === group.rows.length - 1 && styles.answerRowLast]}>
              <Text style={styles.answerLabel}>{row.label}</Text>
              <View style={styles.optionCells} accessibilityRole="radiogroup">
                {row.options.map((opt) => {
                  const selected = answers[row.id] === opt;
                  return (
                    <Pressable
                      key={opt}
                      style={[styles.answerButton, { width: optionWidth(opt) }, selected && styles.answerButtonSelected]}
                      onPress={readOnly ? undefined : () => onAnswer?.(row.id, opt)}
                      disabled={readOnly}
                      accessibilityRole="radio"
                      accessibilityState={{ selected }}
                      accessibilityLabel={`${row.label}: ${opt}`}
                    >
                      <Text style={[styles.answerButtonText, selected && styles.answerButtonTextSelected]} numberOfLines={1}>
                        {opt}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          ))}
        </View>
      ))}
    </View>
  </DocumentSection>
);

const styles = StyleSheet.create({
  checklist: {
    borderWidth: 1,
    borderColor: DocColors.blue100,
    backgroundColor: DocColors.surface,
    borderRadius: 8,
    overflow: 'hidden',
  },
  groupDivider: {
    borderTopWidth: 2,
    borderTopColor: DocColors.blue100,
  },
  groupTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    minHeight: 38,
    paddingVertical: 7,
    paddingHorizontal: 12,
    backgroundColor: DocColors.blue50,
  },
  groupTitle: {
    flex: 1,
    minWidth: 160,
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: DocColors.blue900,
  },
  optionCells: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  optionHeader: {
    textAlign: 'center',
    fontFamily: FontFamily.bold,
    fontSize: 10,
    lineHeight: 14,
    color: DocColors.textSecondary,
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    minHeight: 54,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: DocColors.gray25,
  },
  answerRowLast: {
    borderBottomWidth: 0,
  },
  answerLabel: {
    flex: 1,
    minWidth: 160,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },
  answerButton: {
    height: 32,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: DocColors.gray150,
    backgroundColor: DocColors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  answerButtonSelected: {
    borderColor: DocColors.blue700,
    backgroundColor: DocColors.blue700,
  },
  answerButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  answerButtonTextSelected: {
    color: DocColors.textOnDark,
  },
});

export default QuestionChecklist;
